import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PROJECT_STATUSES, StatusBadge, PriorityBadge, formatDate } from '@/lib/constants';
import { MATERIALS, SURFACE_TREATMENTS, TOLERANCE_CLASSES, MANUFACTURING_PROCESSES, CERTIFICATIONS, translateTerm, translateMaterial, getSpecialPoint } from '@/lib/materials';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Package, Clock, AlertTriangle, CheckCircle, DollarSign, Truck, Plus, Pencil, Send, Globe, ChevronDown, ChevronUp, Download, Upload, X, MessageSquare, Camera, XCircle, Trash2, ShieldAlert, ShieldCheck, FileIcon, Loader2, Box, Inbox, CheckCircle2, Megaphone, Ban, Search } from 'lucide-react';import { toast } from 'sonner';
import NdaSignDialog from '@/components/NdaSignDialog';
import NegotiationDialog from '@/components/NegotiationDialog';
import QuoteDeadlineBadge from '@/components/QuoteDeadlineBadge';
import useFocusItem from '@/hooks/useFocusItem';

// Mini-thumbnail for an attached PDF/image (real preview) or 3D CAD file (icon placeholder).
// Same UX as admin PriceRequestsPage. Click opens the parent details (caller controls onClick).
function AttachmentThumb({ meta = [], onClick, testIdPrefix = 'pr-thumbnail' }) {
  if (!meta || meta.length === 0) return null;
  const previewable = meta.find(a => {
    const ct = (a?.content_type || '').toLowerCase();
    const fn = (a?.original_filename || '').toLowerCase();
    return ct.includes('pdf') || fn.endsWith('.pdf')
      || ct.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(fn);
  });
  const cadFile = !previewable ? meta.find(a => {
    const fn = (a?.original_filename || '').toLowerCase();
    return /\.(step|stp|iges|igs|stl|obj|x_t|x_b|dxf|dwg|sldprt|sldasm|ipt|prt|catpart|catproduct)$/i.test(fn);
  }) : null;
  if (!previewable && !cadFile) return null;
  const tok = localStorage.getItem('pp_access_token') || '';
  const previewUrl = previewable
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/thumbnail/${previewable.id}?width=200&auth=${encodeURIComponent(tok)}`
    : null;
  const cadExt = cadFile ? (cadFile.original_filename.split('.').pop() || '').toUpperCase() : null;
  return (
    <div className="relative shrink-0">
      {previewUrl ? (
        <button type="button" onClick={onClick}
          className="block w-20 h-24 bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden hover:border-amber-500/60 transition-colors group"
          data-testid={`${testIdPrefix}-pdf`}
          title={previewable.original_filename}>
          <img src={previewUrl} alt="plan"
            className="w-full h-full object-cover bg-white group-hover:scale-[1.04] transition-transform"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </button>
      ) : (
        <button type="button" onClick={onClick}
          className="block w-20 h-24 bg-gradient-to-br from-blue-500/5 via-zinc-950 to-zinc-950 border border-zinc-800 rounded-sm overflow-hidden hover:border-blue-500/60 transition-colors group flex flex-col items-center justify-center"
          data-testid={`${testIdPrefix}-cad`}
          title={cadFile.original_filename}>
          <Box className="w-6 h-6 text-blue-400/80 group-hover:text-blue-400 transition-colors" />
          <div className="text-[9px] font-bold uppercase tracking-wider text-blue-400 mt-1">{cadExt}</div>
          <div className="text-[8px] text-zinc-500 mt-0.5">3D / CAO</div>
        </button>
      )}
      {meta.length > 1 && (
        <span className="absolute -top-1.5 -right-1.5 bg-zinc-800 border border-zinc-700 text-zinc-200 text-[10px] font-medium px-1.5 py-0.5 rounded-full shadow-sm"
          title={`${meta.length} fichiers joints`}>
          +{meta.length - 1}
        </span>
      )}
    </div>
  );
}

export default function SubcontractorPortalPage() {
  const { user } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const [projects, setProjects] = useState([]);
  const [priceRequests, setPriceRequests] = useState([]);
  // Combined list so the hook re-runs when either changes — the DOM lookup
  // picks the correct element regardless of which list contains the id.
  useFocusItem([...projects, ...priceRequests]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondDialog, setRespondDialog] = useState(null);
  const [prSearch, setPrSearch] = useState('');
  const [respondForm, setRespondForm] = useState({ price_ttc: 0, currency: 'USD', deadline: '', observations: '', transport_cost: 0, other_fees: 0, quote_number: '', attachments: [] });
  const [responseFiles, setResponseFiles] = useState([]);
  const [uploadingQuote, setUploadingQuote] = useState(false);
  const [declineDialog, setDeclineDialog] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  // Production tracking enhancements
  const [productionDialog, setProductionDialog] = useState(null); // selected shipment for chat/photos view
  const [productionPhotos, setProductionPhotos] = useState([]);
  const [productionMessages, setProductionMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deleteRequestDialog, setDeleteRequestDialog] = useState(null);
  const [deleteRequestReason, setDeleteRequestReason] = useState('');
  const [expandedReq, setExpandedReq] = useState(null);
  const [reqFiles, setReqFiles] = useState({});
  const [shipmentDialog, setShipmentDialog] = useState(false);
  const [editShipment, setEditShipment] = useState(null);
  const [shipForm, setShipForm] = useState({ project_name: '', estimated_end_date: '', tracking_number: '', carrier: '', status: 'in_production', notes: '' });
  // Quality
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityChat, setQualityChat] = useState(null); // selected project for quality chat
  const [qualityMessages, setQualityMessages] = useState([]);
  const [qualityPhotos, setQualityPhotos] = useState([]);
  const [qualityNewMessage, setQualityNewMessage] = useState('');
  const [uploadingQuality, setUploadingQuality] = useState(false);
  // Price-request chat with admin
  const [prChat, setPrChat] = useState(null);
  const [prChatMessages, setPrChatMessages] = useState([]);
  const [prChatNewText, setPrChatNewText] = useState('');
  const [prChatPendingFiles, setPrChatPendingFiles] = useState([]);
  const [prChatUploading, setPrChatUploading] = useState(false);
  const [prNegotiate, setPrNegotiate] = useState(null); // {pr_id, response_id, price, currency, client_name}
  const [negotiations, setNegotiations] = useState([]);
  // Project detail (sub view)
  const [projectDetail, setProjectDetail] = useState(null);
  const [negotiateOpen, setNegotiateOpen] = useState(null);
  const [negotiateForm, setNegotiateForm] = useState({ message: '', counter_price: '' });
  const [projectFiles, setProjectFiles] = useState([]);
  const [projectComments, setProjectComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [detailProgress, setDetailProgress] = useState(0);
  const [detailStatus, setDetailStatus] = useState('');
  const [updatingProject, setUpdatingProject] = useState(false);
  const [readyDialog, setReadyDialog] = useState(false);
  const [readyForm, setReadyForm] = useState({ estimated_end_date: '', tracking_number: '', carrier: '', notes: '' });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('autre');
  // Sub-tab for the Price Requests area: 'to_quote' | 'proposed' | 'declined'
  const [rfqTab, setRfqTab] = useState('to_quote');
  // NDA signature status
  const [ndaStatus, setNdaStatus] = useState({ signed: true, required: false });
  const [ndaDialogOpen, setNdaDialogOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [projRes, prRes, shipRes, scoreRes, negRes] = await Promise.all([
        api.get('/projects'),
        api.get('/price-requests'),
        api.get('/shipments'),
        api.get('/subcontractors/me/score').catch(() => ({ data: null })),
        api.get('/subcontractors/me/negotiations').catch(() => ({ data: [] })),
      ]);
      setProjects(projRes.data);
      setPriceRequests(prRes.data);
      setShipments(shipRes.data);
      if (scoreRes.data) setQualityScore(scoreRes.data.quality_score);
      setNegotiations(negRes.data || []);
      // Fetch NDA signing status (silent on error → assume signed)
      try {
        const { data: nda } = await api.get('/nda/status');
        setNdaStatus(nda);
      } catch (err) { console.debug('[sub-portal] nda status:', err?.message); }
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activeProjects = projects.filter(p => !['termine', 'livre', 'facture'].includes(p.status));
  const completedProjects = projects.filter(p => ['termine', 'livre', 'facture'].includes(p.status));
  const urgentProjects = projects.filter(p => p.priority === 'urgente' && !['termine', 'livre', 'facture'].includes(p.status));

  // Price request expand details + files
  const toggleExpand = async (reqId) => {
    if (expandedReq === reqId) { setExpandedReq(null); return; }
    setExpandedReq(reqId);
    if (!reqFiles[reqId]) {
      try {
        const { data } = await api.get(`/files/list?entity_type=price_request&entity_id=${reqId}`);
        setReqFiles(prev => ({ ...prev, [reqId]: data }));
      } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
    }
  };

  const downloadFile = async (file) => {
    try {
      const response = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download error'); }
  };

  // Price request respond
  const openRespond = async (req) => {
    if (ndaStatus.required && !ndaStatus.signed) {
      toast.error(lang === 'fr'
        ? "Vous devez signer le contrat de confidentialité avant de chiffrer une demande"
        : 'You must sign the confidentiality agreement before quoting a request');
      setNdaDialogOpen(true);
      return;
    }
    setRespondDialog(req);
    if (req.my_response) {
      setRespondForm({
        price_ttc: req.my_response.price_ttc,
        currency: req.my_response.currency || 'USD',
        deadline: req.my_response.deadline || '',
        observations: req.my_response.observations || '',
        transport_cost: req.my_response.transport_cost || 0,
        other_fees: req.my_response.other_fees || 0,
        quote_number: req.my_response.quote_number || '',
        attachments: req.my_response.attachments || [],
      });
      try {
        const { data } = await api.get(`/files/list?entity_type=price_response&entity_id=${req.id}`);
        setResponseFiles(data);
      } catch { setResponseFiles([]); }
    } else {
      setRespondForm({ price_ttc: 0, currency: 'USD', deadline: '', observations: '', transport_cost: 0, other_fees: 0, quote_number: '', attachments: [] });
      setResponseFiles([]);
    }
  };

  const uploadQuoteFile = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingQuote(true);
    try {
      const newIds = [];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'price_response');
        fd.append('entity_id', respondDialog.id);
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        newIds.push(data.id);
        setResponseFiles(prev => [...prev, data]);
      }
      setRespondForm(f => ({ ...f, attachments: [...(f.attachments || []), ...newIds] }));
      toast.success(lang === 'fr' ? 'Devis joint(s)' : 'Quote attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'fr' ? 'Erreur d\'upload' : 'Upload error'));
    } finally {
      setUploadingQuote(false);
    }
  };

  const removeQuoteFile = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setResponseFiles(prev => prev.filter(f => f.id !== fileId));
      setRespondForm(f => ({ ...f, attachments: (f.attachments || []).filter(id => id !== fileId) }));
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
  };

  const submitResponse = async () => {
    try {
      await api.post(`/price-requests/${respondDialog.id}/respond`, respondForm);
      toast.success(lang === 'fr' ? 'Offre envoyée' : 'Offer submitted');
      setRespondDialog(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Decline a price request (subcontractor opt-out with optional reason)
  const submitDecline = async () => {
    try {
      await api.post(`/price-requests/${declineDialog.id}/decline`, { reason: declineReason });
      toast.success(lang === 'fr' ? 'Demande déclinée' : 'Request declined');
      setDeclineDialog(null);
      setDeclineReason('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const undoDecline = async (req) => {
    try {
      await api.delete(`/price-requests/${req.id}/decline`);
      toast.success(lang === 'fr' ? 'Décliné retiré' : 'Decline removed');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Production tracking with photos and messages
  const openProductionView = async (shipment) => {
    setProductionDialog(shipment);
    setNewMessage('');
    try {
      const [shipRes, filesRes] = await Promise.all([
        api.get(`/shipments/${shipment.id}`),
        api.get(`/files/list?entity_type=shipment&entity_id=${shipment.id}`),
      ]);
      setProductionMessages(shipRes.data.messages || []);
      setProductionPhotos(filesRes.data || []);
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
  };

  const sendProductionMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const { data } = await api.post(`/shipments/${productionDialog.id}/messages`, { text: newMessage });
      setProductionMessages(prev => [...prev, data]);
      setNewMessage('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const uploadProductionPhoto = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingPhoto(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'shipment');
        fd.append('entity_id', productionDialog.id);
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setProductionPhotos(prev => [data, ...prev]);
      }
      toast.success(lang === 'fr' ? 'Photo(s) ajoutée(s)' : 'Photo(s) added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    finally { setUploadingPhoto(false); }
  };

  const removeProductionPhoto = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setProductionPhotos(prev => prev.filter(f => f.id !== fileId));
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
  };

  const submitDeleteRequest = async () => {
    try {
      await api.post(`/shipments/${deleteRequestDialog.id}/request-deletion`, { reason: deleteRequestReason });
      toast.success(lang === 'fr' ? 'Demande envoyée à l\'admin' : 'Request sent to admin');
      setDeleteRequestDialog(null);
      setDeleteRequestReason('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const cancelDeleteRequest = async (s) => {
    if (!window.confirm(lang === 'fr' ? 'Annuler la demande de suppression ?' : 'Cancel deletion request?')) return;
    try {
      await api.post(`/shipments/${s.id}/cancel-deletion`);
      toast.success(lang === 'fr' ? 'Demande annulée' : 'Cancelled');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Shipments
  const openNewShipment = () => {
    setEditShipment(null);
    setShipForm({ project_name: '', estimated_end_date: '', tracking_number: '', carrier: '', status: 'in_production', notes: '' });
    setShipmentDialog(true);
  };

  const openEditShipment = (s) => {
    setEditShipment(s);
    setShipForm({
      project_name: s.project_name || '',
      estimated_end_date: s.estimated_end_date ? s.estimated_end_date.split('T')[0] : '',
      tracking_number: s.tracking_number || '',
      carrier: s.carrier || '',
      status: s.status || 'in_production',
      notes: s.notes || '',
    });
    setShipmentDialog(true);
  };

  const saveShipment = async () => {
    try {
      if (editShipment) {
        await api.put(`/shipments/${editShipment.id}`, shipForm);
        toast.success(lang === 'fr' ? 'Expédition mise à jour' : 'Shipment updated');
      } else {
        await api.post('/shipments', shipForm);
        toast.success(lang === 'fr' ? 'Expédition créée' : 'Shipment created');
      }
      setShipmentDialog(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const STATUS_SHIP = {
    in_production: { label: t('inProduction'), color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    verification_requested: { label: lang === 'fr' ? 'Demande de vérification' : 'Verification requested', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    shipped: { label: t('shipped'), color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    delivered: { label: t('delivered'), color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    received: { label: lang === 'fr' ? 'Reçue' : 'Received', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  };

  // Quality projects assigned to this subcontractor
  const qualityProjects = projects.filter(p => p.quality_issue && p.quality_subcontractor_id === user?.id);

  const openQualityChat = async (project) => {
    setQualityChat(project);
    setQualityNewMessage('');
    try {
      const [msgRes, fileRes] = await Promise.all([
        api.get(`/projects/${project.id}/quality/messages`),
        api.get(`/files/list?entity_type=quality&entity_id=${project.id}`),
      ]);
      setQualityMessages(msgRes.data);
      setQualityPhotos(fileRes.data);
    } catch { setQualityMessages([]); setQualityPhotos([]); }
  };

  const sendQualityMessage = async () => {
    if (!qualityNewMessage.trim()) return;
    try {
      const { data } = await api.post(`/projects/${qualityChat.id}/quality/messages`, { text: qualityNewMessage });
      setQualityMessages(prev => [...prev, data]);
      setQualityNewMessage('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const uploadQualityPhoto = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingQuality(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entity_type', 'quality');
        fd.append('entity_id', qualityChat.id);
        const { data } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setQualityPhotos(prev => [data, ...prev]);
      }
      toast.success(lang === 'fr' ? 'Photo(s) ajoutée(s)' : 'Photo(s) added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    finally { setUploadingQuality(false); }
  };

  const removeQualityPhoto = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`);
      setQualityPhotos(prev => prev.filter(f => f.id !== fileId));
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
  };

  // Project detail (sub clicks a project card)
  const openProjectDetail = async (project) => {
    setProjectDetail(project);
    setProjectFiles([]);
    setProjectComments([]);
    setNewComment('');
    setDetailProgress(project.progress || 0);
    setDetailStatus(project.status || '');
    try {
      const [filesRes, commentsRes] = await Promise.all([
        api.get(`/files/list?entity_type=project&entity_id=${project.id}`),
        api.get(`/projects/${project.id}/comments`).catch(() => ({ data: [] })),
      ]);
      setProjectFiles(filesRes.data);
      setProjectComments(commentsRes.data);
    } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
  };

  // Status → suggested progress mapping (shipped is considered ~95% — final delivery confirmation completes it)
  const STATUS_PROGRESS = {
    commande_lancee: 10,
    en_production: 40,
    chez_sous_traitant: 50,
    controle_qualite: 80,
    pret_expedition: 95,
    livre: 100,
    termine: 100,
    bloque: 0,
  };

  const saveProgress = async () => {
    if (!projectDetail) return;
    setUpdatingProject(true);
    try {
      const { data } = await api.put(`/projects/${projectDetail.id}`, { progress: detailProgress, status: detailStatus });
      setProjectDetail(data);
      fetchAll();
      toast.success(lang === 'fr' ? 'Mis à jour' : 'Updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setUpdatingProject(false); }
  };

  // When status changes, auto-update progress
  const onStatusChange = (newStatus) => {
    setDetailStatus(newStatus);
    const suggested = STATUS_PROGRESS[newStatus];
    if (suggested !== undefined) {
      setDetailProgress(suggested);
    }
  };

  const sendProjectComment = async () => {
    if (!newComment.trim() || !projectDetail) return;
    try {
      const { data } = await api.post(`/projects/${projectDetail.id}/comments`, { text: newComment });
      setProjectComments(prev => [...prev, data]);
      setNewComment('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openReadyToShip = () => {
    setReadyForm({
      estimated_end_date: '',
      tracking_number: '',
      carrier: '',
      notes: '',
    });
    setReadyDialog(true);
  };

  const submitReadyToShip = async () => {
    if (!projectDetail) return;
    try {
      // 1) Update project status + progress → 95% (shipped, delivery confirmation remains)
      await api.put(`/projects/${projectDetail.id}`, { status: 'pret_expedition', progress: 95 });
      // 2) Create shipment record
      await api.post('/shipments', {
        project_name: projectDetail.project_name,
        estimated_end_date: readyForm.estimated_end_date || null,
        tracking_number: readyForm.tracking_number,
        carrier: readyForm.carrier,
        status: 'in_production',
        notes: readyForm.notes,
      });
      toast.success(lang === 'fr' ? 'Projet prêt à expédier — basculé dans Gestion expédition' : 'Project ready to ship — moved to Shipment management');
      setReadyDialog(false);
      setProjectDetail(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const downloadProjectFile = async (file) => {
    try {
      const response = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(lang === 'fr' ? 'Téléchargement échoué' : 'Download failed'); }
  };

  const uploadProjectFile = async (fileList) => {
    if (!fileList || fileList.length === 0 || !projectDetail) return;
    setUploadingFile(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/files/upload?entity_type=project&entity_id=${projectDetail.id}&category=${encodeURIComponent(uploadCategory)}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProjectFiles(prev => [data, ...prev]);
      }
      toast.success(lang === 'fr' ? 'Document(s) envoyé(s)' : 'Document(s) sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    finally { setUploadingFile(false); }
  };

  const deleteProjectFile = async (fileId) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer ce document ?' : 'Delete this file?')) return;
    try {
      await api.delete(`/files/${fileId}`);
      setProjectFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success(lang === 'fr' ? 'Supprimé' : 'Deleted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Quality score helpers
  const scoreVal = qualityScore == null ? 100 : qualityScore;
  const scoreColor = scoreVal >= 70 ? 'bg-emerald-500' : scoreVal >= 40 ? 'bg-amber-500' : 'bg-rose-500';
  const scoreText = scoreVal >= 70
    ? { fr: 'Qualité optimale — continuez ainsi !', en: 'Optimal quality — keep it up!' }
    : scoreVal >= 40
    ? { fr: 'À surveiller — quelques problèmes signalés.', en: 'Needs attention — some issues reported.' }
    : scoreVal > 0
    ? { fr: 'Critique — risque de mise en pause des commandes.', en: 'Critical — orders may be paused.' }
    : { fr: 'Liste noire — collaboration suspendue.', en: 'Blacklisted — collaboration suspended.' };

  // Price-request chat with admin
  const openPrChat = async (req) => {
    setPrChat(req);
    setPrChatNewText('');
    setPrChatPendingFiles([]);
    try {
      const { data } = await api.get(`/price-requests/${req.id}/messages`);
      setPrChatMessages(data);
      await api.post(`/price-requests/${req.id}/messages/read`);
      fetchAll();
    } catch { setPrChatMessages([]); }
  };

  const sendPrChatMessage = async () => {
    if (!prChatNewText.trim() && prChatPendingFiles.length === 0) return;
    try {
      await api.post(`/price-requests/${prChat.id}/messages`, {
        text: prChatNewText,
        attachments: prChatPendingFiles.map(f => f.id),
      });
      const { data: msgs } = await api.get(`/price-requests/${prChat.id}/messages`);
      setPrChatMessages(msgs);
      setPrChatNewText('');
      setPrChatPendingFiles([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const uploadPrChatFiles = async (fileList) => {
    if (!fileList || fileList.length === 0 || !prChat) return;
    setPrChatUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/files/upload?entity_type=pr_message&entity_id=${prChat.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setPrChatPendingFiles(prev => [...prev, data]);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload error'); }
    finally { setPrChatUploading(false); }
  };

  const removePrPendingFile = async (fileId) => {
    try { await api.delete(`/files/${fileId}`); } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
    setPrChatPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const downloadPrAttachment = async (file) => {
    try {
      const response = await api.get(`/files/download/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download error'); }
  };


  return (
    <div className="space-y-6" data-testid="subcontractor-portal">
      {/* Header with lang toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {t('subPortal')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t('welcome')} {user?.name || user?.email}</p>
        </div>
        <Button variant="outline" size="sm" onClick={toggleLang} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm gap-2" data-testid="lang-toggle">
          <Globe className="w-4 h-4" />
          {lang === 'fr' ? 'EN' : 'FR'}
        </Button>
      </div>

      {/* NDA — semi-blocking banner for subs who haven't signed yet */}
      {ndaStatus.required && !ndaStatus.signed && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/40 rounded-sm" data-testid="nda-banner">
          <div className="flex items-start gap-3 min-w-0">
            <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-amber-100">
                {lang === 'fr' ? 'Contrat de confidentialité à signer' : 'Confidentiality Agreement required'}
              </div>
              <div className="text-xs text-amber-200/80">
                {lang === 'fr'
                  ? "Vous devez signer le contrat de confidentialité ProdLink avant de pouvoir chiffrer ou décliner une demande de prix."
                  : 'You must sign the ProdLink confidentiality agreement before you can quote or decline any price request.'}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setNdaDialogOpen(true)}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-8 shrink-0"
            data-testid="nda-banner-sign-btn"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
            {lang === 'fr' ? 'Lire et signer' : 'Read and sign'}
          </Button>
        </div>
      )}

      {/* Already signed — small confirmation chip */}
      {ndaStatus.signed && ndaStatus.required && (
        <div className="inline-flex items-center gap-2 text-[11px] text-emerald-400" data-testid="nda-signed-chip">
          <ShieldCheck className="w-3.5 h-3.5" />
          {lang === 'fr' ? 'Contrat de confidentialité signé' : 'Confidentiality agreement signed'}
          {ndaStatus.signed_at && ` · ${new Date(ndaStatus.signed_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}`}
        </div>
      )}

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1 h-auto">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2" data-testid="tab-dashboard">
            {t('dashboard')}
          </TabsTrigger>
          <TabsTrigger value="prices" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2" data-testid="tab-prices">
            {t('priceRequests')}
          </TabsTrigger>
          <TabsTrigger value="negotiations" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2 relative" data-testid="tab-negotiations">
            {t('negotiations')}
            {negotiations.length > 0 && (
              <span className="ml-1.5 bg-amber-500/20 text-amber-300 rounded-full px-1.5 py-0 text-[10px] font-bold border border-amber-500/30" data-testid="negotiations-count">
                {negotiations.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="tracking" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2" data-testid="tab-tracking">
            {t('productionTracking')}
          </TabsTrigger>
          <TabsTrigger value="quality" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-amber-500 text-zinc-400 rounded-sm text-sm px-4 py-2" data-testid="tab-quality">
            {lang === 'fr' ? 'Qualité' : 'Quality'}{qualityProjects.filter(p => !p.quality_resolved).length > 0 ? ` (${qualityProjects.filter(p => !p.quality_resolved).length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-zinc-800 data-[state=active]:text-emerald-400 text-zinc-400 rounded-sm text-sm px-4 py-2" data-testid="tab-completed">
            {lang === 'fr' ? 'Terminés' : 'Completed'} ({completedProjects.length})
          </TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {/* Quality Score Banner */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="quality-score-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${scoreVal >= 70 ? 'bg-emerald-500/10' : scoreVal >= 40 ? 'bg-amber-500/10' : 'bg-rose-500/10'}`}>
                  {scoreVal >= 70 ? <ShieldCheck className="w-5 h-5 text-emerald-400" /> : <ShieldAlert className={`w-5 h-5 ${scoreVal >= 40 ? 'text-amber-400' : 'text-rose-400'}`} />}
                </div>
                <div className="flex-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{lang === 'fr' ? 'Score qualité / Quality score' : 'Quality score / Score qualité'}</p>
                  <p className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="quality-score-value">{scoreVal}%</p>
                </div>
              </div>
              <div className="h-2 bg-zinc-800 rounded-sm overflow-hidden mb-2">
                <div className={`h-full ${scoreColor} transition-all`} style={{ width: `${scoreVal}%` }} />
              </div>
              <p className="text-xs text-zinc-300">
                <span className="font-medium">FR:</span> {scoreText.fr}
              </p>
              <p className="text-xs text-zinc-300">
                <span className="font-medium">EN:</span> {scoreText.en}
              </p>
              <p className="text-[10px] text-zinc-600 mt-2">
                {lang === 'fr'
                  ? 'Barème : livraison parfaite +5% · problème qualité signalé −10%. À 0% une révocation est possible.'
                  : 'Scoring: perfect delivery +5% · reported quality issue −10%. At 0% revocation may apply.'}
              </p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-sky-500/10 rounded-sm flex items-center justify-center"><Package className="w-5 h-5 text-sky-400" /></div>
              <div><p className="text-2xl font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{activeProjects.length}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">{t('projectsInProgress')}</p></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-500/10 rounded-sm flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-rose-400" /></div>
              <div><p className="text-2xl font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{urgentProjects.length}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">{t('urgent')}</p></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/10 rounded-sm flex items-center justify-center"><DollarSign className="w-5 h-5 text-amber-500" /></div>
              <div><p className="text-2xl font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{priceRequests.filter(r => r.status === 'open').length}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">{t('priceRequests')}</p></div>
            </CardContent></Card>
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-sm flex items-center justify-center"><Truck className="w-5 h-5 text-emerald-400" /></div>
              <div><p className="text-2xl font-bold text-zinc-50" style={{fontFamily:'Cabinet Grotesk'}}>{shipments.length}</p><p className="text-xs text-zinc-500 uppercase tracking-wider">{t('productionTracking')}</p></div>
            </CardContent></Card>
          </div>
          {/* Active projects cards */}
          {activeProjects.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-zinc-100 mb-3" style={{fontFamily:'Cabinet Grotesk'}}>{t('myProjects')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sub-projects-grid">
                {activeProjects.map(p => (
                  <Card key={p.id} data-focus-id={p.id} onClick={() => openProjectDetail(p)} className={`bg-zinc-900 border-zinc-800 rounded-sm nexus-card cursor-pointer hover:border-zinc-700 transition-colors ${p.order_confirmed_at ? 'ring-1 ring-emerald-500/30' : ''}`} data-testid={`project-card-${p.id}`}>
                    <CardContent className="p-4">
                      {p.order_confirmed_at && (
                        <div className="mb-2 -mx-4 -mt-4 px-3 py-1.5 bg-emerald-500/15 border-b border-emerald-500/30 rounded-t-sm text-[10px] font-bold uppercase tracking-wider text-emerald-300 flex items-center gap-1.5" data-testid={`project-go-banner-${p.id}`}>
                          <span>✅</span> {lang === 'fr' ? 'GO PRODUCTION — Confirmé par ProdLink' : 'GO PRODUCTION — Confirmed by ProdLink'}
                        </div>
                      )}
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-sm font-semibold text-zinc-100 truncate">{p.project_name}</h3>
                        <PriorityBadge priority={p.priority} />
                      </div>
                      <p className="text-[10px] text-zinc-600 font-mono mb-2">{p.reference}</p>
                      <StatusBadge status={p.status} statusMap={PROJECT_STATUSES} />
                      <div className="mt-2"><Progress value={p.progress || 0} className="h-1.5 bg-zinc-800" /></div>
                      <p className="text-xs text-zinc-500 mt-1">{p.progress || 0}%{p.delivery_date ? ` - ${formatDate(p.delivery_date)}` : ''}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* PRICE REQUESTS TAB */}
        <TabsContent value="prices" className="mt-6 space-y-6">
          {/* Search bar — matches RFQ reference, client name, description, or sub's own quote_number */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="sub-pr-search"
                value={prSearch}
                onChange={(e) => setPrSearch(e.target.value)}
                placeholder={lang === 'fr'
                  ? 'Rechercher par n° de devis, client, description ou référence RFQ…'
                  : 'Search by quote #, client, description or RFQ reference…'}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-9 h-9 text-sm"
              />
            </div>
            {prSearch && (
              <button onClick={() => setPrSearch('')} className="text-xs text-zinc-500 hover:text-zinc-200" data-testid="sub-pr-search-clear">
                {lang === 'fr' ? 'Effacer' : 'Clear'}
              </button>
            )}
          </div>

          {priceRequests.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">{t('noRequests')}</div>
          ) : (() => {
            // Apply search filter
            const q = prSearch.trim().toLowerCase();
            const matchesSearch = (r) => {
              if (!q) return true;
              const haystack = [
                r.id || '',
                (r.id || '').slice(0, 8),
                r.client_name || '',
                r.description || '',
                r.my_response?.quote_number || '',
              ].join(' ').toLowerCase();
              return haystack.includes(q);
            };
            const filteredRequests = priceRequests.filter(matchesSearch);
            const renderCard = (req) => {
            const isProposalMade = req.status === 'proposition_faite';
            const isClientDeclined = req.status === 'client_declined';
            const orderAlreadyPlaced = !!req.order_already_placed;
            return (
            <Card key={req.id} data-focus-id={req.id} className={`bg-zinc-900 border rounded-sm nexus-card ${orderAlreadyPlaced ? 'border-rose-500/60 ring-1 ring-rose-500/20' : isClientDeclined ? 'border-rose-500/40 opacity-80' : isProposalMade ? 'border-violet-500/30' : 'border-zinc-800'}`} data-testid={`price-req-${req.id}`}>
              <CardContent className="p-5">
                {orderAlreadyPlaced && (
                  <div className="mb-3 bg-rose-500/15 border border-rose-500/50 rounded-sm p-2.5 nexus-pulse" data-testid={`order-placed-banner-${req.id}`}>
                    <p className="text-xs text-rose-100 font-bold flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                      <span className="text-rose-300 font-semibold">FR :</span> 🔴 Commande déjà passée chez ProdLink — positionnez-vous rapidement !
                    </p>
                    <p className="text-xs text-rose-100 font-bold mt-0.5 ml-5">
                      <span className="text-rose-300 font-semibold">EN :</span> Order already placed at ProdLink — submit your quote ASAP!
                    </p>
                  </div>
                )}
                {isClientDeclined && (
                  <div className="mb-3 bg-rose-500/10 border border-rose-500/30 rounded-sm p-2.5" data-testid={`client-declined-banner-${req.id}`}>
                    <p className="text-xs text-rose-200 font-medium">
                      <span className="text-rose-400 font-semibold">FR :</span> Le client a décliné cette consultation ({req.client_declined_reason_label || 'sans raison précisée'}).
                      {req.client_declined_detail && <> Détail : <em>{req.client_declined_detail}</em></>}
                    </p>
                    <p className="text-xs text-rose-200 mt-0.5">
                      <span className="text-rose-400 font-semibold">EN :</span> The client declined this RFQ ({req.client_declined_reason_label || '—'}).
                      {req.client_declined_detail && <> Detail: <em>{req.client_declined_detail}</em></>}
                    </p>
                  </div>
                )}
                {isProposalMade && req.is_best_positioned && (
                  <div className="mb-3 bg-gradient-to-r from-amber-500/15 to-amber-500/5 border border-amber-500/40 rounded-sm p-2.5 ring-1 ring-amber-500/20" data-testid={`best-positioned-banner-${req.id}`}>
                    <p className="text-xs text-amber-100 font-semibold flex items-center gap-1.5">
                      <span className="text-base">🏆</span>
                      <span className="text-amber-300 font-semibold">FR :</span> Vous êtes le mieux placé — votre offre a été présentée au client. On revient vers vous dès qu'il valide.
                    </p>
                    <p className="text-xs text-amber-100 font-semibold mt-0.5 ml-6">
                      <span className="text-amber-300 font-semibold">EN :</span> You're the best-placed quote — your offer has been presented to the client. We'll get back to you as soon as they confirm.
                    </p>
                  </div>
                )}
                {isProposalMade && !req.is_best_positioned && (
                  <div className="mb-3 bg-violet-500/10 border border-violet-500/30 rounded-sm p-2.5" data-testid={`proposal-banner-${req.id}`}>
                    <p className="text-xs text-violet-200 font-medium">
                      <span className="text-violet-400 font-semibold">FR :</span> Proposition transmise au client. L'équipe ProdLink revient vers vous rapidement si la commande est confirmée.
                    </p>
                    <p className="text-xs text-violet-200 mt-0.5">
                      <span className="text-violet-400 font-semibold">EN :</span> Proposal sent to the client. The ProdLink team will get back to you shortly if the order is confirmed.
                    </p>
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <AttachmentThumb
                    meta={req.attachments_meta || []}
                    onClick={() => toggleExpand(req.id)}
                    testIdPrefix={`sub-pr-thumb-${req.id}`}
                  />
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-zinc-100">{req.client_name}</h3>
                      {isProposalMade && req.is_best_positioned && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/25 text-amber-200 border border-amber-500/50" data-testid={`best-badge-${req.id}`} title="Vous êtes le mieux placé / You're the best-placed quote">
                          🏆 {lang === 'fr' ? 'Mieux placé' : 'Best placed'}
                        </span>
                      )}
                      {isProposalMade && !req.is_best_positioned && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-violet-500/20 text-violet-300 border border-violet-500/30">
                          {lang === 'fr' ? 'Proposition faite' : 'Proposal sent'}
                        </span>
                      )}
                      <QuoteDeadlineBadge deadline={req.quote_deadline} lang={lang} dense />
                      {(() => {
                        // Retention indicator (mirrors backend): auto-delete after
                        // 30 d on "proposition_faite" and 7 d on "client_declined".
                        const days = req.status === 'proposition_faite' ? 30 : req.status === 'client_declined' ? 7 : null;
                        const anchor = req.status === 'proposition_faite' ? req.proposition_made_at : req.status === 'client_declined' ? req.client_declined_at : null;
                        if (!days || !anchor) return null;
                        const anchorDate = new Date(anchor);
                        if (isNaN(anchorDate.getTime())) return null;
                        const deleteAt = new Date(anchorDate.getTime() + days * 24 * 60 * 60 * 1000);
                        const remaining = Math.ceil((deleteAt - Date.now()) / (24 * 60 * 60 * 1000));
                        if (remaining <= 0) return null;
                        const cls = remaining <= 3
                          ? 'bg-rose-500/10 text-rose-300 border-rose-500/30'
                          : remaining <= 7
                            ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                            : 'bg-zinc-800/40 text-zinc-400 border-zinc-700';
                        const title = lang === 'fr' ? `Suppression automatique le ${deleteAt.toLocaleDateString('fr-FR')} · ${days} j après ${req.status === 'proposition_faite' ? 'proposition faite' : 'refus client'}` : `Auto-deletes on ${deleteAt.toLocaleDateString('en-GB')} · ${days} d after ${req.status === 'proposition_faite' ? 'proposal sent' : 'client decline'}`;
                        return (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${cls}`} title={title} data-testid={`retention-${req.id}`}>
                            🕓 {lang === 'fr' ? `J-${remaining}` : `${remaining}d left`}
                          </span>
                        );
                      })()}
                      <span className="text-xs text-zinc-500">{req.response_count} {t('offersReceived')}</span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-2 line-clamp-3 whitespace-pre-wrap">{req.description}</p>
                    <div className="flex gap-4 text-xs text-zinc-500">
                      {req.desired_deadline && <span>{t('desiredDeadline')}: {formatDate(req.desired_deadline)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button size="sm" variant="outline" onClick={() => toggleExpand(req.id)}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8" data-testid={`details-btn-${req.id}`}>
                      {expandedReq === req.id ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                      {lang === 'fr' ? 'Détails' : 'Details'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openPrChat(req)}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm text-xs h-8 relative" data-testid={`pr-chat-btn-${req.id}`}>
                      <MessageSquare className="w-3.5 h-3.5 mr-1" />
                      {lang === 'fr' ? 'Messages' : 'Messages'}
                      {(req.my_unread_count || 0) > 0 ? (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold bg-rose-500 text-zinc-50 animate-pulse" data-testid={`sub-unread-badge-${req.id}`}>{req.my_unread_count}</span>
                      ) : (req.my_messages_count || 0) > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-sm text-[10px] font-bold bg-amber-500 text-zinc-950">{req.my_messages_count}</span>
                      )}
                    </Button>
                    {req.my_decline ? (
                      <Button size="sm" variant="outline" onClick={() => undoDecline(req)}
                        className="border-rose-500/30 bg-rose-500/5 text-rose-400 hover:bg-rose-500/10 rounded-sm text-xs h-8" data-testid={`undo-decline-${req.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" />
                        {lang === 'fr' ? 'Décliné — Annuler' : 'Declined — Undo'}
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => {
                          if (ndaStatus.required && !ndaStatus.signed) {
                            toast.error(lang === 'fr'
                              ? "Vous devez signer le contrat de confidentialité avant de décliner"
                              : 'You must sign the confidentiality agreement before declining');
                            setNdaDialogOpen(true);
                            return;
                          }
                          setDeclineDialog(req); setDeclineReason('');
                        }}
                          className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-rose-400 rounded-sm text-xs h-8" data-testid={`decline-btn-${req.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          {lang === 'fr' ? 'Décliner' : 'Decline'}
                        </Button>
                        <Button size="sm" onClick={() => openRespond(req)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8 font-medium" data-testid={`respond-btn-${req.id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" />
                          {req.my_response ? t('modifyOffer') : t('respond')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {expandedReq === req.id && (
                  <ErrorBoundary>
                    <div className="border-t border-zinc-800 pt-3 mt-2 space-y-3 nexus-fade-in">
                      {/* Laser marking (visible to sub) */}
                      {req.laser_marking_enabled && (
                        <div className="bg-sky-500/5 border border-sky-500/20 rounded-sm p-3" data-testid={`sub-laser-${req.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-sky-400 font-semibold mb-1">{lang === 'fr' ? 'Marquage au laser requis' : 'Laser marking required'}</p>
                          {req.laser_marking_text ? (
                            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{req.laser_marking_text}</p>
                          ) : (
                            <p className="text-xs text-zinc-500 italic">{lang === 'fr' ? 'Texte non spécifié' : 'Text not specified'}</p>
                          )}
                        </div>
                      )}
                      {/* Target price (visible to sub) */}
                      {req.price_target != null && (
                        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3" data-testid={`sub-price-target-${req.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-semibold mb-1">{lang === 'fr' ? 'Objectif de prix' : 'Target price'}</p>
                          <p className="text-base text-emerald-300 font-bold font-mono">{Number(req.price_target).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: 'EUR' })}</p>
                        </div>
                      )}
                      {/* Quality specs */}
                      {(req.general_tolerance || req.surface_finish || req.heat_treatment || req.marking_serialization || req.inspection_norm || req.packaging || req.production_type || req.additional_specs) && (
                        <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3" data-testid={`sub-quality-${req.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">{lang === 'fr' ? 'Spécifications qualité' : 'Quality specs'}</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            {req.general_tolerance && <div><span className="text-zinc-500">{lang === 'fr' ? 'Tolérance gén.' : 'General tol.'} :</span> <span className="text-zinc-200">{req.general_tolerance}</span></div>}
                            {req.surface_finish && <div><span className="text-zinc-500">{lang === 'fr' ? 'État surface' : 'Surface finish'} :</span> <span className="text-zinc-200">{req.surface_finish}</span></div>}
                            {req.heat_treatment && <div><span className="text-zinc-500">{lang === 'fr' ? 'Traitement therm.' : 'Heat treatment'} :</span> <span className="text-zinc-200">{req.heat_treatment}</span></div>}
                            {req.marking_serialization && <div><span className="text-zinc-500">{lang === 'fr' ? 'Marquage/série' : 'Marking/serial'} :</span> <span className="text-zinc-200">{req.marking_serialization}</span></div>}
                            {req.inspection_norm && <div><span className="text-zinc-500">{lang === 'fr' ? 'Inspection' : 'Inspection'} :</span> <span className="text-zinc-200">{req.inspection_norm}</span></div>}
                            {req.packaging && <div><span className="text-zinc-500">{lang === 'fr' ? 'Conditionnement' : 'Packaging'} :</span> <span className="text-zinc-200">{req.packaging}</span></div>}
                            {req.production_type && <div><span className="text-zinc-500">{lang === 'fr' ? 'Type' : 'Type'} :</span> <span className="text-zinc-200 capitalize">{req.production_type}</span></div>}
                            {req.additional_specs && <div className="md:col-span-2"><span className="text-zinc-500">{lang === 'fr' ? 'Autres' : 'Other'} :</span> <span className="text-zinc-200">{req.additional_specs}</span></div>}
                          </div>
                        </div>
                      )}
                      {/* Parts */}
                      {Array.isArray(req.parts) && req.parts.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{lang === 'fr' ? 'Pièces' : 'Parts'} ({req.parts.length})</p>
                        <div className="space-y-2">
                          {req.parts.map((p, i) => (
                            <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
                              <span className="text-sm font-medium text-zinc-200">{p.name || `${lang === 'fr' ? 'Pièce' : 'Part'} ${i + 1}`}</span>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs mt-1.5">
                                {p.material && <div><span className="text-zinc-500">{lang === 'fr' ? 'Matériau' : 'Material'}:</span> <span className="text-zinc-300">{translateMaterial(p.material, lang)}</span></div>}
                                {p.designation && <div><span className="text-zinc-500">{lang === 'fr' ? 'Désignation' : 'Grade'}:</span> <span className="text-zinc-300">{p.designation}</span></div>}
                                {p.quantity && <div><span className="text-zinc-500">{lang === 'fr' ? 'Quantité' : 'Qty'}:</span> <span className="text-zinc-300">{p.quantity}</span></div>}
                                {p.surface_treatment && p.surface_treatment !== 'Aucun' && <div><span className="text-zinc-500">{lang === 'fr' ? 'Surface' : 'Surface'}:</span> <span className="text-zinc-300">{translateTerm(p.surface_treatment, SURFACE_TREATMENTS, lang)}</span></div>}
                                {p.color_ral && <div><span className="text-zinc-500">{lang === 'fr' ? 'RAL/Couleur' : 'RAL/Colour'}:</span> <span className="text-amber-400 font-medium">{p.color_ral}</span></div>}
                                {p.tolerance && <div><span className="text-zinc-500">{lang === 'fr' ? 'Tolérance' : 'Tolerance'}:</span> <span className="text-zinc-300">{translateTerm(p.tolerance, TOLERANCE_CLASSES, lang)}</span></div>}
                                {p.process && <div><span className="text-zinc-500">{lang === 'fr' ? 'Procédé' : 'Process'}:</span> <span className="text-zinc-300">{translateTerm(p.process, MANUFACTURING_PROCESSES, lang)}</span></div>}
                              </div>
                              {(p.special_points || []).length > 0 && (
                                <div className="mt-2 pt-2 border-t border-amber-500/20 bg-amber-500/5 -mx-3 -mb-3 px-3 pb-2.5 rounded-b-sm">
                                  <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1.5 font-bold flex items-center gap-1">
                                    ⚠ {lang === 'fr' ? 'Points particuliers de fabrication' : 'Special manufacturing points'}
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {(p.special_points || []).map(spKey => {
                                      const meta = getSpecialPoint(spKey, lang);
                                      if (!meta) return null;
                                      return (
                                        <span key={spKey} className={`text-[11px] px-2 py-0.5 rounded-sm border ${meta.cls.on}`} title={meta.hint}>
                                          {meta.label}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {p.notes && <p className="text-xs text-zinc-500 mt-2 italic">{lang === 'fr' ? 'Note' : 'Note'}: {p.notes}</p>}
                              {(p.ai_plan_summary_fr || p.ai_plan_summary_en) && (
                                <div className="mt-2 pt-2 border-t border-blue-500/20 bg-blue-500/5 -mx-3 -mb-3 px-3 pb-2.5 rounded-b-sm">
                                  <p className="text-[10px] uppercase tracking-wider text-blue-400 mb-1.5 font-bold flex items-center gap-1">
                                    ✦ {lang === 'fr' ? 'Points critiques du plan' : 'Critical points from the drawing'}
                                  </p>
                                  <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed" data-testid={`sub-part-ai-summary-${p.name || 'x'}`}>
                                    {lang === 'en'
                                      ? (p.ai_plan_summary_en || p.ai_plan_summary_fr)
                                      : (p.ai_plan_summary_fr || p.ai_plan_summary_en)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Certifications */}
                    {req.certifications?.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{lang === 'fr' ? 'Certifications' : 'Certifications'}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {req.certifications.map((c, i) => <span key={i} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm">{translateTerm(c, CERTIFICATIONS, lang)}</span>)}
                        </div>
                      </div>
                    )}
                    {/* Files */}
                    {reqFiles[req.id]?.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">{lang === 'fr' ? 'Fichiers à télécharger' : 'Files to download'}</p>
                        <div className="space-y-1.5">
                          {reqFiles[req.id].map(file => (
                            <button key={file.id} onClick={() => downloadFile(file)}
                              className="flex items-center gap-2 w-full text-left bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 hover:border-zinc-700 transition-colors group" data-testid={`download-${file.id}`}>
                              <Download className="w-4 h-4 text-blue-400 group-hover:text-blue-300" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-zinc-300 truncate">{file.original_filename}</p>
                                <p className="text-[10px] text-zinc-600">{(file.size / 1024).toFixed(0)} KB</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {!reqFiles[req.id]?.length && !req.parts?.length && (
                      <p className="text-xs text-zinc-600 text-center py-2">{lang === 'fr' ? 'Aucun détail supplémentaire' : 'No additional details'}</p>
                    )}
                    </div>
                  </ErrorBoundary>
                )}

                {req.my_decline && (
                  <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-3 mt-2" data-testid={`my-decline-${req.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-3.5 h-3.5 text-rose-400" />
                      <span className="text-xs font-medium text-rose-400 uppercase tracking-wider">{lang === 'fr' ? 'Vous avez décliné' : 'You declined'}</span>
                    </div>
                    {req.my_decline.reason && <p className="text-xs text-zinc-400 italic">"{req.my_decline.reason}"</p>}
                  </div>
                )}
                {req.my_response && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 mt-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{t('yourOffer')}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                          <span className="text-amber-400 font-semibold">
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: req.my_response.currency || 'USD' }).format(
                              (req.my_response.total_price != null ? req.my_response.total_price : (req.my_response.price_ttc || 0) + (req.my_response.transport_cost || 0) + (req.my_response.other_fees || 0))
                            )}
                            <span className="text-[10px] text-zinc-500 ml-1">{lang === 'fr' ? 'total' : 'total'}</span>
                          </span>
                          <span className="text-zinc-400">{t('deadline')}: {req.my_response.deadline || '-'}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-[11px] text-zinc-500 mt-1">
                          <span>{lang === 'fr' ? 'Prix' : 'Price'}: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: req.my_response.currency || 'USD' }).format(req.my_response.price_ttc || 0)}</span>
                          {(req.my_response.transport_cost || 0) > 0 && <span>{lang === 'fr' ? 'Transport' : 'Shipping'}: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: req.my_response.currency || 'USD' }).format(req.my_response.transport_cost)}</span>}
                          {(req.my_response.other_fees || 0) > 0 && <span>{lang === 'fr' ? 'Autres frais' : 'Other fees'}: {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: req.my_response.currency || 'USD' }).format(req.my_response.other_fees)}</span>}
                          {(req.my_response.attachments || []).length > 0 && <span className="text-blue-400">{(req.my_response.attachments || []).length} {lang === 'fr' ? 'devis joint(s)' : 'quote(s) attached'}</span>}
                        </div>
                        {req.my_response.observations && <p className="text-xs text-zinc-500 mt-1">{req.my_response.observations}</p>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setPrNegotiate({ pr_id: req.id, response_id: req.my_response.id, price: (req.my_response.total_price != null ? req.my_response.total_price : (req.my_response.price_ttc || 0) + (req.my_response.transport_cost || 0) + (req.my_response.other_fees || 0)), currency: req.my_response.currency || 'EUR', client_name: req.client_name || '' }); }}
                        className="bg-amber-500/5 text-amber-300 border-amber-500/30 hover:bg-amber-500/15 rounded-sm text-xs h-8 flex-shrink-0"
                        data-testid={`sub-negotiate-pr-${req.id}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5 mr-1" />
                        {lang === 'fr' ? 'Négocier' : 'Negotiate'}
                        {(req.my_response.negotiations || []).length > 0 && (
                          <span className="ml-1.5 bg-amber-500/30 text-amber-100 rounded-full px-1.5 py-0 text-[10px] font-bold">
                            {(req.my_response.negotiations || []).length}
                          </span>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            );
            }; // end renderCard
            const declined = filteredRequests.filter(r => r.my_decline);
            const declinedIds = new Set(declined.map(r => r.id));
            // Completed = admin has forwarded the proposal to the final client (status=proposition_faite),
            // and the sub hasn't declined. The sub can't act anymore, just track.
            const completed = filteredRequests.filter(r =>
              !declinedIds.has(r.id) && r.status === 'proposition_faite'
            );
            const completedIds = new Set(completed.map(r => r.id));
            // Proposed = the sub submitted a price+deadline, awaiting admin decision (still 'open').
            const inProgress = filteredRequests.filter(r =>
              !declinedIds.has(r.id) && !completedIds.has(r.id) && r.my_response
            );
            const reservedIds = new Set([...declinedIds, ...completedIds, ...inProgress.map(r => r.id)]);
            const openRfqs = filteredRequests.filter(r => !reservedIds.has(r.id));

            const RFQ_TABS = [
              { key: 'to_quote',  labelFr: 'À chiffrer',       labelEn: 'To quote',     icon: Inbox,        accent: 'amber',   items: openRfqs,
                emptyFr: 'Aucune demande en attente de votre chiffrage.',
                emptyEn: 'No requests waiting for your quote.' },
              { key: 'proposed',  labelFr: 'Mes propositions', labelEn: 'My proposals', icon: Send,         accent: 'blue',    items: inProgress,
                emptyFr: 'Aucune proposition en attente. Vos chiffrages envoyés à l\'admin apparaîtront ici.',
                emptyEn: 'No pending proposals. Your quotes sent to admin will appear here.' },
              { key: 'completed', labelFr: 'Terminées',        labelEn: 'Completed',    icon: CheckCircle2, accent: 'emerald', items: completed,
                emptyFr: 'Aucune demande terminée. Quand l\'admin transmet votre offre au client, elle apparaît ici.',
                emptyEn: 'No completed requests yet. When the admin forwards your offer to the client, it lands here.' },
              { key: 'declined',  labelFr: 'Refusées',         labelEn: 'Declined',     icon: Ban,          accent: 'rose',    items: declined,
                emptyFr: 'Aucune demande déclinée. Les refus apparaissent ici — vous pouvez les ré-ouvrir à tout moment.',
                emptyEn: 'No declined requests. Refusals appear here — you can re-open them anytime.' },
            ];
            const RFQ_ACCENTS = {
              amber:   { active: 'text-amber-300 border-amber-500/40 bg-amber-500/10',     badge: 'bg-amber-500/20 text-amber-300' },
              blue:    { active: 'text-blue-300 border-blue-500/40 bg-blue-500/10',        badge: 'bg-blue-500/20 text-blue-300' },
              emerald: { active: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', badge: 'bg-emerald-500/20 text-emerald-300' },
              rose:    { active: 'text-rose-300 border-rose-500/40 bg-rose-500/10',        badge: 'bg-rose-500/20 text-rose-300' },
            };
            const activeRfqTab = RFQ_TABS.find(t => t.key === rfqTab) || RFQ_TABS[0];

            return (<>
              {/* Sub-tab bar */}
              <div className="flex items-center gap-1 border-b border-zinc-800 -mb-px overflow-x-auto" data-testid="sub-rfq-tabs">
                {RFQ_TABS.map(tabCfg => {
                  const accent = RFQ_ACCENTS[tabCfg.accent];
                  const isActive = rfqTab === tabCfg.key;
                  const count = tabCfg.items.length;
                  const Icon = tabCfg.icon;
                  return (
                    <button
                      key={tabCfg.key}
                      onClick={() => setRfqTab(tabCfg.key)}
                      data-testid={`sub-rfq-tab-${tabCfg.key}`}
                      className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        isActive ? `${accent.active} border-current` : 'text-zinc-500 hover:text-zinc-200 border-transparent'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span>{lang === 'fr' ? tabCfg.labelFr : tabCfg.labelEn}</span>
                      {count > 0 && (
                        <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm text-[11px] font-semibold ${isActive ? accent.badge : 'bg-zinc-800 text-zinc-400'}`} data-testid={`sub-rfq-tab-count-${tabCfg.key}`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active tab content */}
              <section className="space-y-3" data-testid={`sub-rfq-section-${activeRfqTab.key}`}>
                {activeRfqTab.items.length === 0 ? (
                  <p className="text-xs text-zinc-600 italic pl-1 py-4">
                    {lang === 'fr' ? activeRfqTab.emptyFr : activeRfqTab.emptyEn}
                  </p>
                ) : activeRfqTab.items.map(renderCard)}
              </section>
            </>);
          })()}
        </TabsContent>

        {/* NEGOTIATIONS TAB */}
        <TabsContent value="negotiations" className="mt-6 space-y-4" data-testid="negotiations-tab-content">
          <div>
            <h2 className="text-2xl font-semibold text-zinc-100 mb-1" style={{ fontFamily: 'Cabinet Grotesk' }}>
              {t('negotiations')}
            </h2>
            <p className="text-sm text-zinc-500">{t('negotiationsIntro')}</p>
          </div>
          {negotiations.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="negotiations-empty">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
              {t('negotiationsEmpty')}
            </div>
          ) : (
            <div className="space-y-3">
              {negotiations.map((n) => {
                const isPrice = n.type === 'price_request';
                const yourTurn = n.waiting_for === 'sub';
                const key = isPrice ? `pr-${n.pr_id}-${n.response_id}` : `order-${n.project_id}`;
                const fmtCur = (v, c) => {
                  if (v == null || v === '') return '—';
                  try { return new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', { style: 'currency', currency: c || 'EUR' }).format(Number(v)); } catch { return `${v} ${c || ''}`; }
                };
                const fmtDate = (iso) => {
                  if (!iso) return '';
                  try { return new Date(iso).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
                };
                return (
                  <div key={key} className={`bg-zinc-900 border rounded-sm p-4 hover:border-amber-500/40 transition-colors ${yourTurn ? 'border-amber-500/40' : 'border-zinc-800'}`} data-testid={`negotiation-item-${key}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-medium uppercase tracking-wider ${isPrice ? 'bg-blue-500/10 text-blue-300 border-blue-500/30' : 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'}`}>
                            {isPrice ? t('negotiationTypePrice') : t('negotiationTypeOrder')}
                          </span>
                          <span className={`inline-block px-2 py-0.5 rounded-sm border text-[10px] font-medium ${yourTurn ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' : 'bg-zinc-800/40 text-zinc-400 border-zinc-700'}`} data-testid={`negotiation-status-${key}`}>
                            {yourTurn ? t('yourTurn') : t('waitingReply')}
                          </span>
                          {n.count > 1 && <span className="text-[10px] text-zinc-500">· {n.count} {lang === 'fr' ? 'messages' : 'messages'}</span>}
                        </div>
                        <h3 className="text-base font-semibold text-zinc-100 truncate">
                          {n.client_name || '—'}
                          {n.project_name && <span className="text-zinc-500 font-normal"> · {n.project_name}</span>}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-0.5">Réf {n.reference} · {fmtDate(n.last_at)}</p>
                        {n.last_message && (
                          <div className="mt-2 text-sm text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 line-clamp-2" data-testid={`negotiation-last-msg-${key}`}>
                            <span className="text-[10px] text-zinc-500 uppercase tracking-wider mr-1.5">{t('lastMessage')}:</span>
                            {n.last_message}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-2">
                          {n.initial_price != null && (
                            <span className="text-zinc-500">
                              {t('initialOffer')}: <span className="font-mono text-zinc-300">{fmtCur(n.initial_price, n.currency)}</span>
                            </span>
                          )}
                          {n.amount_ht != null && (
                            <span className="text-zinc-500">
                              {lang === 'fr' ? 'Montant' : 'Amount'}: <span className="font-mono text-zinc-300">{fmtCur(n.amount_ht, n.currency)}</span>
                            </span>
                          )}
                          {n.last_counter_price != null && (
                            <span className="text-amber-400">
                              {t('counterOffer')}: <span className="font-mono font-semibold">{fmtCur(n.last_counter_price, n.currency)}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {isPrice ? (
                          <Button
                            size="sm"
                            onClick={() => setPrNegotiate({ pr_id: n.pr_id, response_id: n.response_id, price: n.initial_price, currency: n.currency, client_name: n.client_name })}
                            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8 font-medium"
                            data-testid={`negotiation-open-${key}`}
                          >
                            <MessageSquare className="w-3.5 h-3.5 mr-1" /> {t('openNegotiation')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => {
                              const p = projects.find(pp => pp.id === n.project_id);
                              if (p) openProjectDetail(p);
                            }}
                            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8 font-medium"
                            data-testid={`negotiation-open-${key}`}
                          >
                            <Package className="w-3.5 h-3.5 mr-1" /> {t('openNegotiation')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* PRODUCTION TRACKING TAB */}
        <TabsContent value="tracking" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-500">{lang === 'fr' ? 'Suivi de mes productions' : 'My productions tracking'}</p>
            <Button onClick={openNewShipment} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium text-sm" data-testid="new-production-btn">
              <Plus className="w-4 h-4 mr-2" /> {lang === 'fr' ? 'Nouvelle production' : 'New production'}
            </Button>
          </div>
          {shipments.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm">
              <Package className="w-12 h-12 mx-auto mb-3 text-zinc-700" />
              {lang === 'fr' ? 'Aucune production en cours' : 'No production in progress'}
            </div>
          ) : (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{t('projectName')}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{t('status')}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{t('estimatedEndDate')}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{t('carrier')}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{t('trackingNumber')}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500">{lang === 'fr' ? 'Échanges' : 'Updates'}</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shipments.map(s => (
                    <TableRow key={s.id} className="border-zinc-800/50 nexus-table-row">
                      <TableCell className="text-sm text-zinc-200 font-medium">{s.project_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${STATUS_SHIP[s.status]?.color || ''}`}>
                          {STATUS_SHIP[s.status]?.label || s.status}
                        </span>
                        {s.status === 'received' && s.auto_delete_in_days !== undefined && (
                          <div className={`text-[10px] mt-0.5 ${s.auto_delete_in_days <= 3 ? 'text-rose-400' : 'text-zinc-600'}`} data-testid={`sub-auto-delete-${s.id}`}>
                            {s.auto_delete_in_days === 0
                              ? (lang === 'fr' ? 'Suppression auto. à minuit' : 'Auto-delete at midnight')
                              : (lang === 'fr' ? `Suppression auto. dans ${s.auto_delete_in_days}j` : `Auto-delete in ${s.auto_delete_in_days}d`)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-400">{formatDate(s.estimated_end_date)}</TableCell>
                      <TableCell className="text-sm text-zinc-300">{s.carrier || '-'}</TableCell>
                      <TableCell className="text-sm text-blue-400 font-mono">{s.tracking_number || '-'}</TableCell>
                      <TableCell className="text-xs text-zinc-500">
                        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{(s.messages || []).length}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openProductionView(s)} title={lang === 'fr' ? 'Photos & échanges' : 'Photos & messages'} className="p-1.5 text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`view-production-${s.id}`}>
                            <MessageSquare className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => openEditShipment(s)} title={lang === 'fr' ? 'Modifier' : 'Edit'} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors" data-testid={`edit-shipment-${s.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {s.deletion_requested ? (
                            <button onClick={() => cancelDeleteRequest(s)} title={lang === 'fr' ? 'Annuler la demande de suppression' : 'Cancel deletion request'} className="p-1.5 text-amber-400 hover:text-amber-300 transition-colors animate-pulse" data-testid={`cancel-delete-${s.id}`}>
                              <Clock className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button onClick={() => { setDeleteRequestDialog(s); setDeleteRequestReason(''); }} title={lang === 'fr' ? 'Demander la suppression' : 'Request deletion'} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors" data-testid={`request-delete-${s.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {s.deletion_requested && (
                          <div className="text-[10px] text-amber-400 mt-0.5">{lang === 'fr' ? 'Suppression en attente' : 'Deletion pending'}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* QUALITY TAB */}
        <TabsContent value="quality" className="mt-6 space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <p className="text-sm text-zinc-300">
              <span className="font-semibold">FR :</span> {lang === 'fr' ? 'Voici les projets pour lesquels ProdLink a signalé un problème qualité. Échangez ici pour trouver une solution rapidement.' : 'Projects where ProdLink reported a quality issue. Discuss here to find a solution quickly.'}
            </p>
            <p className="text-sm text-zinc-300 mt-1">
              <span className="font-semibold">EN :</span> Below are the projects flagged by ProdLink for quality issues. Use the chat to resolve them quickly.
            </p>
          </div>
          {qualityProjects.length === 0 ? (
            <div className="text-center py-16 text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-sm">
              <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-emerald-500/40" />
              {lang === 'fr' ? 'Aucun problème qualité signalé. Excellent travail !' : 'No quality issue reported. Excellent work!'}
            </div>
          ) : (
            qualityProjects.map(p => (
              <Card key={p.id} data-focus-id={p.id} className={`bg-zinc-900 border rounded-sm nexus-card ${p.quality_resolved ? 'border-emerald-500/20' : 'border-rose-500/30'}`} data-testid={`sub-quality-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-zinc-100">{p.project_name}</h3>
                        <span className="text-xs text-zinc-500 font-mono">{p.reference}</span>
                        {p.quality_resolved ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{lang === 'fr' ? 'Résolu' : 'Resolved'}</span>
                        ) : (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose-500/10 text-rose-400 border border-rose-500/30 animate-pulse">{lang === 'fr' ? 'Ouvert' : 'Open'}</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 mb-2">{lang === 'fr' ? 'Réf.' : 'Ref.'}: <span className="text-zinc-300 font-mono">{p.reference}</span></p>
                      {p.quality_description && <p className="text-sm text-zinc-300 bg-zinc-950 border border-zinc-800 rounded-sm p-2 mb-2">{p.quality_description}</p>}
                      <p className="text-[11px] text-zinc-600">{lang === 'fr' ? 'Signalé le' : 'Flagged on'} {formatDate(p.quality_flagged_at)}</p>
                    </div>
                    <Button size="sm" onClick={() => openQualityChat(p)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-xs h-8" data-testid={`sub-open-quality-chat-${p.id}`}>
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> {lang === 'fr' ? 'Échanger' : 'Discuss'} ({(p.quality_messages || []).length})
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* COMPLETED PROJECTS TAB (read-only history) */}
        <TabsContent value="completed" className="mt-6 space-y-4">
          {completedProjects.length === 0 ? (
            <div className="text-center py-16 text-zinc-500">
              {lang === 'fr' ? 'Aucun projet terminé pour le moment.' : 'No completed projects yet.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="completed-projects-grid">
              {completedProjects.map(p => (
                <Card key={p.id} data-focus-id={p.id} onClick={() => openProjectDetail(p)} className="bg-zinc-900 border border-emerald-500/20 rounded-sm nexus-card cursor-pointer hover:border-emerald-500/40 transition-colors" data-testid={`completed-card-${p.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-zinc-100 truncate">{p.project_name}</h3>
                      <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                    </div>
                    <p className="text-[10px] text-zinc-600 font-mono mb-2">{p.reference}</p>
                    <StatusBadge status={p.status} statusMap={PROJECT_STATUSES} />
                    {p.delivery_date && (
                      <p className="text-xs text-zinc-500 mt-2">
                        {lang === 'fr' ? 'Livraison prévue : ' : 'Delivery: '}{formatDate(p.delivery_date)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Quality Chat Dialog */}
      <Dialog open={!!qualityChat} onOpenChange={() => setQualityChat(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sub-quality-chat-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {lang === 'fr' ? 'Qualité' : 'Quality'} — {qualityChat?.project_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {qualityChat?.client_name}
              {qualityChat?.quality_resolved && <span className="ml-2 text-emerald-400">({lang === 'fr' ? 'Résolu' : 'Resolved'})</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            {qualityChat?.quality_description && (
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">{lang === 'fr' ? 'Problème signalé par ProdLink' : 'Issue reported by ProdLink'}</p>
                <p className="text-sm text-zinc-200">{qualityChat.quality_description}</p>
              </div>
            )}
            {/* Photos */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Photos / Pièces jointes' : 'Photos / Attachments'}</Label>
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> {lang === 'fr' ? 'Ajouter' : 'Upload'}
                  <input data-testid="sub-quality-photo-input" type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => uploadQualityPhoto(e.target.files)} disabled={uploadingQuality} />
                </label>
              </div>
              {qualityPhotos.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-3 bg-zinc-950 border border-zinc-800 border-dashed rounded-sm">{lang === 'fr' ? 'Aucune photo' : 'No photos'}</p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {qualityPhotos.map(f => <PhotoThumb key={f.id} file={f} onRemove={() => removeQualityPhoto(f.id)} />)}
                </div>
              )}
            </div>
            {/* Messages */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> {lang === 'fr' ? 'Échanges' : 'Messages'} ({qualityMessages.length})</Label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="sub-quality-messages-list">
                {qualityMessages.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4">{lang === 'fr' ? 'Aucun message. Démarrez la conversation avec ProdLink.' : 'No messages yet. Start the conversation with ProdLink.'}</p>
                ) : qualityMessages.map(m => {
                  const mine = m.sender_role === 'subcontractor';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                        <p className="text-[10px] text-zinc-500 mb-0.5">
                          <span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                          <span className="ml-2">{formatDate(m.created_at)}</span>
                        </p>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!qualityChat?.quality_resolved && (
                <div className="flex items-center gap-2">
                  <Input data-testid="sub-quality-message-input" value={qualityNewMessage} onChange={(e) => setQualityNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendQualityMessage())}
                    placeholder={lang === 'fr' ? 'Écrire un message à ProdLink...' : 'Type a message to ProdLink...'}
                    className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
                  <Button data-testid="sub-quality-send-btn" onClick={sendQualityMessage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQualityChat(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{lang === 'fr' ? 'Fermer' : 'Close'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Respond Dialog */}
      <Dialog open={!!respondDialog} onOpenChange={() => setRespondDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="respond-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{respondDialog?.my_response ? t('modifyOffer') : t('respond')}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">{respondDialog?.client_name} - {respondDialog?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">{t('priceTTC')} *</Label>
                <Input data-testid="respond-price" type="number" step="0.01" value={respondForm.price_ttc} onChange={(e) => setRespondForm(f => ({ ...f, price_ttc: parseFloat(e.target.value) || 0 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">{t('currency')}</Label>
                <Select value={respondForm.currency} onValueChange={(v) => setRespondForm(f => ({ ...f, currency: v }))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="EUR">EUR (&#8364;)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Transport' : 'Shipping cost'}</Label>
                <Input data-testid="respond-transport" type="number" step="0.01" value={respondForm.transport_cost}
                  onChange={(e) => setRespondForm(f => ({ ...f, transport_cost: parseFloat(e.target.value) || 0 }))}
                  placeholder="0" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Autres frais (PayPal, douane...)' : 'Other fees (PayPal, customs...)'}</Label>
                <Input data-testid="respond-other-fees" type="number" step="0.01" value={respondForm.other_fees}
                  onChange={(e) => setRespondForm(f => ({ ...f, other_fees: parseFloat(e.target.value) || 0 }))}
                  placeholder="0" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              </div>
            </div>
            {/* Total preview */}
            <div className="bg-zinc-950 border border-amber-500/30 rounded-sm px-3 py-2 flex items-center justify-between">
              <span className="text-xs text-zinc-500 uppercase tracking-wider">{lang === 'fr' ? 'Total (prix + transport + autres)' : 'Total (price + shipping + other)'}</span>
              <span className="text-base font-bold text-amber-400" data-testid="respond-total" style={{fontFamily:'Cabinet Grotesk'}}>
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: respondForm.currency || 'USD' }).format(
                  (respondForm.price_ttc || 0) + (respondForm.transport_cost || 0) + (respondForm.other_fees || 0)
                )}
              </span>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Votre numéro de devis' : 'Your quote number'} <span className="text-zinc-600 text-xs">({lang === 'fr' ? 'optionnel — pour retrouver cette demande' : 'optional — to find this RFQ later'})</span></Label>
              <Input data-testid="respond-quote-number" value={respondForm.quote_number || ''} onChange={(e) => setRespondForm(f => ({ ...f, quote_number: e.target.value }))} placeholder={lang === 'fr' ? 'Ex: DEV-2026-0042' : 'Ex: Q-2026-0042'} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('deadline')}</Label>
              <Input data-testid="respond-deadline" value={respondForm.deadline} onChange={(e) => setRespondForm(f => ({ ...f, deadline: e.target.value }))} placeholder={lang === 'fr' ? 'Ex: 3 semaines' : 'Ex: 3 weeks'} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('observations')}</Label>
              <Textarea data-testid="respond-observations" value={respondForm.observations} onChange={(e) => setRespondForm(f => ({ ...f, observations: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[60px]" />
            </div>
            {/* Quote PDF attachment */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Joindre un devis (PDF)' : 'Attach a quote (PDF)'}</Label>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> {lang === 'fr' ? 'Téléverser' : 'Upload'}
                  <input data-testid="respond-file-input" type="file" multiple className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx" onChange={(e) => uploadQuoteFile(e.target.files)} disabled={uploadingQuote} />
                </label>
                {uploadingQuote && <span className="text-[11px] text-zinc-500 animate-pulse">{lang === 'fr' ? 'Envoi...' : 'Uploading...'}</span>}
                <span className="text-[10px] text-zinc-600">{lang === 'fr' ? 'Visible uniquement par l\'admin ProdLink' : 'Only visible to ProdLink admin'}</span>
              </div>
              {responseFiles.length > 0 && (
                <div className="space-y-1 mt-2">
                  {responseFiles.map(file => (
                    <div key={file.id} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-sm px-2.5 py-1.5">
                      <span className="text-xs text-zinc-300 truncate" data-testid={`uploaded-quote-${file.id}`}>{file.original_filename}</span>
                      <button onClick={() => removeQuoteFile(file.id)} className="text-zinc-600 hover:text-rose-400 transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{t('cancel')}</Button>
            <Button data-testid="submit-response" onClick={submitResponse} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">{t('submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shipment Dialog */}
      <Dialog open={shipmentDialog} onOpenChange={setShipmentDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="shipment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{editShipment ? t('update') : t('addShipment')}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">{lang === 'fr' ? 'Renseignez les informations de votre production.' : 'Enter your production details.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('projectName')} *</Label>
              <Input data-testid="ship-project" value={shipForm.project_name} onChange={(e) => setShipForm(f => ({ ...f, project_name: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('status')}</Label>
              <Select value={shipForm.status} onValueChange={(v) => setShipForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="ship-status" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="in_production">{t('inProduction')}</SelectItem>
                  <SelectItem value="verification_requested">{lang === 'fr' ? 'Demande de vérification' : 'Verification requested'}</SelectItem>
                  <SelectItem value="shipped">{t('shipped')}</SelectItem>
                  <SelectItem value="delivered">{t('delivered')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('estimatedEndDate')}</Label>
              <Input type="date" value={shipForm.estimated_end_date} onChange={(e) => setShipForm(f => ({ ...f, estimated_end_date: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('carrier')}</Label>
              <Select value={shipForm.carrier} onValueChange={(v) => setShipForm(f => ({ ...f, carrier: v === 'none' ? '' : v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue placeholder={lang === 'fr' ? 'Sélectionner' : 'Select'} /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">-</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="FEDEX">FedEx</SelectItem>
                  <SelectItem value="OTHER">{lang === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('trackingNumber')}</Label>
              <Input data-testid="ship-tracking" value={shipForm.tracking_number} onChange={(e) => setShipForm(f => ({ ...f, tracking_number: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{t('notes')}</Label>
              <Textarea value={shipForm.notes} onChange={(e) => setShipForm(f => ({ ...f, notes: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShipmentDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{t('cancel')}</Button>
            <Button data-testid="save-shipment" onClick={saveShipment} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={!!declineDialog} onOpenChange={() => setDeclineDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'fr' ? 'Décliner la demande' : 'Decline request'}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {declineDialog?.client_name} — {declineDialog?.description?.slice(0, 80)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}</Label>
            <Textarea data-testid="decline-reason" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
              placeholder={lang === 'fr' ? 'Ex: Surcharge atelier, matière non maîtrisée, délai trop court...' : 'Ex: Workshop overload, material unfamiliar, deadline too short...'}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[80px]" />
            <p className="text-[11px] text-zinc-600">{lang === 'fr' ? "L'admin verra que vous avez décliné. La raison reste optionnelle." : "Admin will see you declined. The reason is optional."}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{t('cancel')}</Button>
            <Button data-testid="submit-decline" onClick={submitDecline} className="bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 rounded-sm font-medium">
              <XCircle className="w-4 h-4 mr-1.5" /> {lang === 'fr' ? 'Décliner' : 'Decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deletion request dialog */}
      <Dialog open={!!deleteRequestDialog} onOpenChange={() => setDeleteRequestDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="delete-request-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'fr' ? 'Demander la suppression' : 'Request deletion'}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {deleteRequestDialog?.project_name}
              <br />
              {lang === 'fr' ? "L'admin recevra une demande à valider avant la suppression définitive." : "Admin will receive a request to validate before final deletion."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2">
            <Label className="text-zinc-400 text-sm">{lang === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}</Label>
            <Textarea data-testid="delete-request-reason" value={deleteRequestReason} onChange={(e) => setDeleteRequestReason(e.target.value)}
              placeholder={lang === 'fr' ? 'Ex: doublon, projet annulé...' : 'Ex: duplicate, cancelled project...'}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[70px]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRequestDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{t('cancel')}</Button>
            <Button data-testid="submit-delete-request" onClick={submitDeleteRequest} className="bg-rose-500/10 text-rose-400 border border-rose-500/30 hover:bg-rose-500/20 rounded-sm font-medium">
              <Trash2 className="w-4 h-4 mr-1.5" /> {lang === 'fr' ? 'Envoyer la demande' : 'Send request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Production view (chat + photos) */}
      <Dialog open={!!productionDialog} onOpenChange={() => setProductionDialog(null)}>        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="production-view-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{productionDialog?.project_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {lang === 'fr' ? 'Photos & échanges avec ProdLink sur cette production' : 'Photos & messages with ProdLink for this production'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-5">
            {/* Photos */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><Camera className="w-4 h-4" /> {lang === 'fr' ? 'Photos / Documents' : 'Photos / Documents'}</Label>
                <label className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> {lang === 'fr' ? 'Ajouter' : 'Upload'}
                  <input data-testid="prod-photo-input" type="file" multiple accept="image/*,.pdf" className="hidden" onChange={(e) => uploadProductionPhoto(e.target.files)} disabled={uploadingPhoto} />
                </label>
              </div>
              {productionPhotos.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-4 bg-zinc-950 border border-zinc-800 border-dashed rounded-sm">
                  {lang === 'fr' ? 'Aucune photo. Ajoutez des photos de la production pour ProdLink.' : 'No photos yet. Upload production photos for ProdLink.'}
                </p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {productionPhotos.map(f => (
                    <PhotoThumb key={f.id} file={f} onRemove={() => removeProductionPhoto(f.id)} />
                  ))}
                </div>
              )}
            </div>

            {/* Chat */}
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> {lang === 'fr' ? 'Échanges' : 'Messages'} ({productionMessages.length})</Label>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="messages-list">
                {productionMessages.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4">{lang === 'fr' ? 'Aucun message. Démarrez la conversation.' : 'No messages yet. Start the conversation.'}</p>
                ) : productionMessages.map(m => {
                  const mine = m.sender_id === user?.id || m.sender_role === 'subcontractor';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                        <p className="text-[10px] text-zinc-500 mb-0.5">
                          <span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                          <span className="ml-2">{formatDate(m.created_at)}</span>
                        </p>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Input data-testid="prod-message-input" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendProductionMessage())}
                  placeholder={lang === 'fr' ? 'Écrire un message...' : 'Type a message...'}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
                <Button data-testid="send-message-btn" onClick={sendProductionMessage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductionDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{lang === 'fr' ? 'Fermer' : 'Close'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Project detail dialog (sub view) */}
      <Dialog open={!!projectDetail} onOpenChange={() => setProjectDetail(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sub-project-detail-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{projectDetail?.project_name}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm font-mono">{projectDetail?.reference}</DialogDescription>
          </DialogHeader>
          {projectDetail && (
            <ErrorBoundary>
            <div className="space-y-4 py-2">
              {/* Order confirmation banner (from admin) → sub can accept / negotiate */}
              {projectDetail.order_confirmed_at && (
                <div className={`rounded-sm border p-3 ${projectDetail.order_accepted_at ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-emerald-500/8 border-emerald-500/30'}`} data-testid="sub-order-confirmation-banner">
                  <div className="text-sm font-bold text-emerald-200 flex items-center gap-2">
                    <span className="text-lg">🟢</span>
                    {projectDetail.order_accepted_at
                      ? (lang === 'fr' ? 'GO PRODUCTION — Vous avez accepté' : 'GO PRODUCTION — You accepted')
                      : projectDetail.order_negotiating
                      ? (lang === 'fr' ? 'GO envoyé — Négociation en cours' : 'GO sent — Negotiation ongoing')
                      : (lang === 'fr' ? 'GO PRODUCTION reçu de ProdLink' : 'GO PRODUCTION received from ProdLink')}
                  </div>
                  <p className="text-xs text-emerald-100/80 mt-1">
                    {projectDetail.order_accepted_at
                      ? (lang === 'fr'
                          ? `Commande confirmée le ${new Date(projectDetail.order_accepted_at).toLocaleDateString('fr-FR')}. Vous pouvez lancer la production sereinement.`
                          : `Order confirmed on ${new Date(projectDetail.order_accepted_at).toLocaleDateString('en-GB')}. You can safely start production.`)
                      : (lang === 'fr'
                          ? 'Merci de confirmer votre acceptation pour verrouiller la commande, ou de négocier si un point vous gêne (prix, délai, etc.).'
                          : 'Please confirm your acceptance to lock the order, or negotiate if anything blocks you (price, delay, etc.).')}
                  </p>
                  {!projectDetail.order_accepted_at && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        onClick={async () => {
                          if (!window.confirm(lang === 'fr' ? 'Confirmer votre acceptation de cette commande ?' : 'Confirm your acceptance of this order?')) return;
                          try {
                            await api.post(`/projects/${projectDetail.id}/sub-accept`);
                            toast.success(lang === 'fr' ? '✅ Commande acceptée' : '✅ Order accepted');
                            setProjectDetail({ ...projectDetail, order_accepted_at: new Date().toISOString(), order_negotiating: false });
                            fetchAll();
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
                        }}
                        className="bg-emerald-500 hover:bg-emerald-400 text-white rounded-sm text-sm font-semibold"
                        data-testid="sub-accept-order-btn"
                      >
                        ✅ {lang === 'fr' ? 'Accepter la commande' : 'Accept order'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setNegotiateOpen(projectDetail)}
                        className="border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10 rounded-sm text-sm"
                        data-testid="sub-negotiate-btn"
                      >
                        🗨 {lang === 'fr' ? 'Négocier / Demander une modif' : 'Negotiate / Request change'}
                      </Button>
                    </div>
                  )}
                  {projectDetail.order_negotiating && projectDetail.order_negotiation?.message && (
                    <div className="mt-3 bg-zinc-950/60 border border-amber-500/30 rounded-sm p-2 text-xs text-amber-100">
                      <div className="text-[10px] uppercase text-amber-400 tracking-wider mb-1">{lang === 'fr' ? 'Votre message' : 'Your message'}</div>
                      {projectDetail.order_negotiation.message}
                      {projectDetail.order_negotiation.counter_price && (
                        <div className="mt-1 text-amber-200 font-mono">
                          {lang === 'fr' ? 'Contre-proposition' : 'Counter-offer'}: {projectDetail.order_negotiation.counter_price.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Status + priority */}
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={projectDetail.status} statusMap={PROJECT_STATUSES} />
                <PriorityBadge priority={projectDetail.priority} />
                {projectDetail.desired_deadline && (
                  <span className="text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 px-2 py-1 rounded-sm">
                    {lang === 'fr' ? 'Délai souhaité' : 'Deadline'}: {formatDate(projectDetail.desired_deadline)}
                  </span>
                )}
                {projectDetail.delivery_date && (
                  <span className="text-xs text-amber-400 bg-amber-500/5 border border-amber-500/20 px-2 py-1 rounded-sm">
                    {lang === 'fr' ? 'Livraison' : 'Delivery'}: {formatDate(projectDetail.delivery_date)}
                  </span>
                )}
              </div>

              {/* Description / Instructions */}
              {projectDetail.description && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">{lang === 'fr' ? 'Instructions & description' : 'Instructions & description'}</p>
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3">
                    <p className="text-sm text-zinc-200 whitespace-pre-wrap">{projectDetail.description}</p>
                  </div>
                </div>
              )}

              {/* Parts (technical specs) */}
              {(projectDetail.parts || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">{lang === 'fr' ? 'Pièces à produire' : 'Parts to produce'}</p>
                  <div className="space-y-2">
                    {projectDetail.parts.map((part, idx) => (
                      <div key={idx} className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-1 text-xs" data-testid={`sub-project-part-${idx}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-zinc-100">{part.name || `${lang === 'fr' ? 'Pièce' : 'Part'} ${idx + 1}`}</p>
                          {part.quantity && <span className="text-amber-400 font-mono">x {part.quantity}</span>}
                        </div>
                        {part.designation && <p className="text-zinc-400">{part.designation}</p>}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-zinc-500">
                          {part.material && <div><span className="text-zinc-600">{lang === 'fr' ? 'Matière' : 'Material'}:</span> <span className="text-zinc-300">{part.material}</span></div>}
                          {part.ral_color && <div><span className="text-zinc-600">RAL:</span> <span className="text-zinc-300">{part.ral_color}</span></div>}
                          {part.finish && <div><span className="text-zinc-600">{lang === 'fr' ? 'Finition' : 'Finish'}:</span> <span className="text-zinc-300">{part.finish}</span></div>}
                          {part.tolerance && <div><span className="text-zinc-600">{lang === 'fr' ? 'Tolérance' : 'Tolerance'}:</span> <span className="text-zinc-300">{part.tolerance}</span></div>}
                        </div>
                        {part.notes && <p className="text-[11px] text-zinc-500 italic mt-1">{part.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {(projectDetail.certifications || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">{lang === 'fr' ? 'Certifications requises' : 'Required certifications'}</p>
                  <div className="flex flex-wrap gap-1">
                    {projectDetail.certifications.map((c, i) => (
                      <span key={i} className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-1 rounded-sm">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Files (3D, plans, invoices, etc.) */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
                    <FileIcon className="w-3 h-3" /> {lang === 'fr' ? 'Documents & fichiers' : 'Documents & files'} ({projectFiles.length})
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Select value={uploadCategory} onValueChange={setUploadCategory}>
                      <SelectTrigger data-testid="upload-category" className="bg-zinc-900 border-zinc-800 text-zinc-300 rounded-sm h-7 text-[11px] w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="facture">{lang === 'fr' ? 'Facture' : 'Invoice'}</SelectItem>
                        <SelectItem value="bon_livraison">{lang === 'fr' ? 'Bon de livraison' : 'Delivery note'}</SelectItem>
                        <SelectItem value="certificat">{lang === 'fr' ? 'Certificat' : 'Certificate'}</SelectItem>
                        <SelectItem value="rapport_controle">{lang === 'fr' ? 'Rapport contrôle' : 'Inspection report'}</SelectItem>
                        <SelectItem value="photo">{lang === 'fr' ? 'Photo' : 'Photo'}</SelectItem>
                        <SelectItem value="autre">{lang === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1 px-2 py-1 bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm text-[11px] font-medium cursor-pointer transition-colors h-7">
                      {uploadingFile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {lang === 'fr' ? 'Envoyer' : 'Send'}
                      <input data-testid="sub-project-upload" type="file" multiple className="hidden" onChange={(e) => uploadProjectFile(e.target.files)} disabled={uploadingFile} />
                    </label>
                  </div>
                </div>
                {projectFiles.length === 0 ? (
                  <p className="text-xs text-zinc-600 text-center py-4 bg-zinc-950 border border-zinc-800 border-dashed rounded-sm">
                    {lang === 'fr' ? 'Aucun document. Envoyez une facture, un bon de livraison, des photos...' : 'No files. Upload an invoice, delivery note, photos...'}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {projectFiles.map(f => {
                      const isMine = f.uploaded_by === user?.id;
                      const CAT_LABELS = {
                        facture: { fr: 'Facture', en: 'Invoice', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
                        bon_livraison: { fr: 'BL', en: 'BL', cls: 'bg-sky-500/10 text-sky-400 border-sky-500/30' },
                        certificat: { fr: 'Certif.', en: 'Cert.', cls: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
                        rapport_controle: { fr: 'Rapport', en: 'Report', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
                        photo: { fr: 'Photo', en: 'Photo', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
                      };
                      const cat = CAT_LABELS[f.category];
                      return (
                        <div key={f.id} className="flex items-center gap-2 bg-zinc-950 border border-zinc-800 hover:border-amber-500/50 rounded-sm p-2 transition-colors group" data-testid={`sub-project-file-${f.id}`}>
                          <FileIcon className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 flex-shrink-0" />
                          <button onClick={() => downloadProjectFile(f)} className="flex-1 min-w-0 text-left">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm text-zinc-200 truncate">{f.original_filename}</p>
                              {cat && (
                                <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cat.cls} flex-shrink-0`}>
                                  {lang === 'fr' ? cat.fr : cat.en}
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-600">
                              {((f.size || 0) / 1024).toFixed(1)} KB
                              {f.uploaded_by_name && ` · ${f.uploaded_by_name}`}
                              {f.created_at && ` · ${formatDate(f.created_at)}`}
                            </p>
                          </button>
                          <Download className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 flex-shrink-0" />
                          {isMine && (
                            <button onClick={() => deleteProjectFile(f.id)} data-testid={`sub-delete-file-${f.id}`} className="text-zinc-600 hover:text-rose-400 flex-shrink-0" title={lang === 'fr' ? 'Supprimer' : 'Delete'}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Progress + status control (sub can update) */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{lang === 'fr' ? 'Avancement & statut' : 'Progress & status'}</p>
                  <p className={`text-base font-mono font-bold ${detailProgress >= 95 ? 'text-emerald-400' : detailProgress >= 50 ? 'text-amber-400' : 'text-sky-400'}`}>{detailProgress}%</p>
                </div>

                {/* Visual progress bar with stepped milestones */}
                <div className="relative">
                  <div className="h-3 bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${detailProgress >= 95 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : detailProgress >= 50 ? 'bg-gradient-to-r from-sky-500 via-amber-500 to-amber-400' : 'bg-gradient-to-r from-sky-600 to-sky-400'}`}
                      style={{ width: `${detailProgress}%` }}
                    />
                  </div>
                  {/* Milestones */}
                  <div className="flex justify-between mt-1.5 text-[9px] text-zinc-600 uppercase tracking-wider">
                    <span className={detailProgress >= 10 ? 'text-sky-400' : ''}>{lang === 'fr' ? 'Lancé' : 'Started'}</span>
                    <span className={detailProgress >= 40 ? 'text-sky-400' : ''}>{lang === 'fr' ? 'Production' : 'Production'}</span>
                    <span className={detailProgress >= 80 ? 'text-amber-400' : ''}>{lang === 'fr' ? 'Qualité' : 'QC'}</span>
                    <span className={detailProgress >= 95 ? 'text-emerald-400' : ''}>{lang === 'fr' ? 'Expédié' : 'Shipped'}</span>
                    <span className={detailProgress >= 100 ? 'text-emerald-400 font-bold' : ''}>{lang === 'fr' ? 'Livré' : 'Delivered'}</span>
                  </div>
                </div>

                <input type="range" min="0" max="100" step="5" value={detailProgress} onChange={(e) => setDetailProgress(parseInt(e.target.value))}
                  data-testid="sub-progress-slider"
                  className="w-full h-2 bg-zinc-800 rounded-sm appearance-none cursor-pointer accent-amber-500" />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={detailStatus} onValueChange={onStatusChange}>
                    <SelectTrigger data-testid="sub-status-select" className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-xs">
                      <SelectValue placeholder={lang === 'fr' ? 'Statut' : 'Status'} />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="commande_lancee">{lang === 'fr' ? 'Commande lancée (10%)' : 'Order launched (10%)'}</SelectItem>
                      <SelectItem value="en_production">{lang === 'fr' ? 'En production (40%)' : 'In production (40%)'}</SelectItem>
                      <SelectItem value="controle_qualite">{lang === 'fr' ? 'Contrôle qualité (80%)' : 'Quality control (80%)'}</SelectItem>
                      <SelectItem value="pret_expedition">{lang === 'fr' ? 'Prêt à expédier (95%)' : 'Ready to ship (95%)'}</SelectItem>
                      <SelectItem value="bloque">{lang === 'fr' ? 'Bloqué' : 'Blocked'}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={saveProgress} disabled={updatingProject} data-testid="sub-save-progress" className="bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-sm text-xs h-9">
                    {updatingProject ? (lang === 'fr' ? 'Mise à jour...' : 'Updating...') : (lang === 'fr' ? 'Mettre à jour' : 'Update')}
                  </Button>
                </div>
                <Button onClick={openReadyToShip} data-testid="sub-ready-to-ship" className="w-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm text-sm h-10">
                  <Truck className="w-4 h-4 mr-2" />
                  {lang === 'fr' ? 'Prêt à expédier — basculer vers Gestion expédition' : 'Ready to ship — move to Shipment management'}
                </Button>
              </div>

              {/* Q&A with admin */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
                  <MessageSquare className="w-3 h-3" /> {lang === 'fr' ? 'Questions à ProdLink' : 'Questions to ProdLink'} ({projectComments.length})
                </p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="sub-project-comments">
                  {projectComments.length === 0 ? (
                    <p className="text-xs text-zinc-600 text-center py-3">{lang === 'fr' ? 'Aucun échange. Posez vos questions ci-dessous.' : 'No messages yet. Ask your questions below.'}</p>
                  ) : projectComments.map(c => {
                    const mine = c.author_role === 'subcontractor';
                    return (
                      <div key={c.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-2.5 py-1.5 rounded-sm text-xs border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                          <p className="text-[9px] text-zinc-500 mb-0.5"><span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{c.author}</span> · {formatDate(c.date)}</p>
                          <p className="whitespace-pre-wrap">{c.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <Input data-testid="sub-comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendProjectComment())}
                    placeholder={lang === 'fr' ? 'Poser une question à l\'admin...' : 'Ask a question to the admin...'}
                    className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-xs" />
                  <Button data-testid="sub-comment-send" onClick={sendProjectComment} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm h-9 px-3">
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>

      {/* Ready to ship dialog */}
      <Dialog open={readyDialog} onOpenChange={setReadyDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="sub-ready-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk' }}>
              {lang === 'fr' ? 'Prêt à expédier' : 'Ready to ship'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {lang === 'fr' ? 'Le projet sera basculé dans Gestion expédition avec les infos ci-dessous.' : 'The project will move to Shipment management with the info below.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{lang === 'fr' ? 'Date estimée de fin' : 'Estimated end date'}</Label>
              <Input type="date" value={readyForm.estimated_end_date} onChange={(e) => setReadyForm(f => ({ ...f, estimated_end_date: e.target.value }))} data-testid="ready-end-date" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{lang === 'fr' ? 'Transporteur' : 'Carrier'}</Label>
              <Input value={readyForm.carrier} onChange={(e) => setReadyForm(f => ({ ...f, carrier: e.target.value }))} placeholder="DHL, UPS, Chronopost..." data-testid="ready-carrier" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{lang === 'fr' ? 'N° de suivi' : 'Tracking number'}</Label>
              <Input value={readyForm.tracking_number} onChange={(e) => setReadyForm(f => ({ ...f, tracking_number: e.target.value }))} placeholder="ABC123..." data-testid="ready-tracking" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{lang === 'fr' ? 'Notes' : 'Notes'}</Label>
              <Textarea value={readyForm.notes} onChange={(e) => setReadyForm(f => ({ ...f, notes: e.target.value }))} data-testid="ready-notes" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadyDialog(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">{lang === 'fr' ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={submitReadyToShip} data-testid="ready-submit" className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm">
              <Truck className="w-4 h-4 mr-1.5" /> {lang === 'fr' ? 'Confirmer' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price-request chat dialog (with admin) */}
      <Dialog open={!!prChat} onOpenChange={() => setPrChat(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="sub-pr-chat-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {lang === 'fr' ? 'Messages avec ProdLink' : 'Messages with ProdLink'} — {prChat?.client_name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {lang === 'fr' ? 'Demandez des informations complémentaires sur cette demande de prix' : 'Request additional information about this price request'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 space-y-2 max-h-72 overflow-y-auto" data-testid="sub-pr-chat-list">
              {prChatMessages.length === 0 ? (
                <p className="text-xs text-zinc-600 text-center py-6">{lang === 'fr' ? 'Aucun message. Posez votre question à ProdLink.' : 'No messages yet. Ask ProdLink your question.'}</p>
              ) : prChatMessages.map(m => {
                const mine = m.sender_role === 'subcontractor';
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3 py-1.5 rounded-sm text-sm border ${mine ? 'bg-amber-500/10 text-amber-100 border-amber-500/20' : 'bg-zinc-900 text-zinc-200 border-zinc-800'}`}>
                      <p className="text-[10px] text-zinc-500 mb-0.5">
                        <span className={mine ? 'text-amber-400' : 'text-zinc-400'}>{m.sender_name}</span>
                        <span className="ml-2">{formatDate(m.created_at)}</span>
                      </p>
                      {m.text && <p className="whitespace-pre-wrap">{m.text}</p>}
                      {(m.attachments_meta || []).length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          {m.attachments_meta.map(f => (
                            <button key={f.id} onClick={() => downloadPrAttachment(f)} className="flex items-center gap-1.5 text-[11px] text-blue-400 hover:text-blue-300 underline" data-testid={`sub-attachment-${f.id}`}>
                              <FileIcon className="w-3 h-3" /> {f.original_filename} <span className="text-zinc-600">({((f.size || 0) / 1024).toFixed(0)} KB)</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {prChatPendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="sub-pr-chat-pending-files">
                {prChatPendingFiles.map(f => (
                  <span key={f.id} className="inline-flex items-center gap-1 text-[11px] bg-zinc-900 border border-zinc-800 text-zinc-300 px-2 py-1 rounded-sm">
                    <FileIcon className="w-3 h-3" /> {f.original_filename}
                    <button onClick={() => removePrPendingFile(f.id)} className="ml-1 text-zinc-500 hover:text-rose-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="flex items-center justify-center w-9 h-9 bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 cursor-pointer transition-colors" title={lang === 'fr' ? 'Joindre fichiers (STEP, PDF...)' : 'Attach files (STEP, PDF...)'}>
                {prChatUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <input data-testid="sub-pr-chat-file-input" type="file" multiple className="hidden" onChange={(e) => uploadPrChatFiles(e.target.files)} disabled={prChatUploading} />
              </label>
              <Input data-testid="sub-pr-chat-input" value={prChatNewText} onChange={(e) => setPrChatNewText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendPrChatMessage())}
                placeholder={lang === 'fr' ? 'Votre question...' : 'Your question...'}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
              <Button data-testid="sub-pr-chat-send" onClick={sendPrChatMessage} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Negotiation dialog */}
      <Dialog open={!!negotiateOpen} onOpenChange={(v) => { if (!v) { setNegotiateOpen(null); setNegotiateForm({ message: '', counter_price: '' }); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="negotiate-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300">🗨 {lang === 'fr' ? 'Négocier la commande' : 'Negotiate order'}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {lang === 'fr'
                ? 'Expliquez à ProdLink ce qui bloque (prix, délai, spec…). Optionnel : proposez un nouveau prix HT.'
                : 'Explain to ProdLink what\'s blocking (price, deadline, spec…). Optional: propose a new price (excl. VAT).'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1 block">{lang === 'fr' ? 'Votre message' : 'Your message'}</label>
              <textarea
                value={negotiateForm.message}
                onChange={(e) => setNegotiateForm(f => ({ ...f, message: e.target.value }))}
                placeholder={lang === 'fr' ? 'Ex: Le délai est trop court, pouvons-nous passer à 4 semaines ?' : 'Ex: Delay too short, can we move to 4 weeks?'}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 min-h-[100px]"
                data-testid="negotiate-message"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1 block">{lang === 'fr' ? 'Contre-proposition prix HT (optionnel)' : 'Counter price excl. VAT (optional)'}</label>
              <input
                type="number"
                step="0.01"
                value={negotiateForm.counter_price}
                onChange={(e) => setNegotiateForm(f => ({ ...f, counter_price: e.target.value }))}
                placeholder="€"
                className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100"
                data-testid="negotiate-counter-price"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setNegotiateOpen(null); setNegotiateForm({ message: '', counter_price: '' }); }} className="border-zinc-700 text-zinc-300 rounded-sm">
              {lang === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={async () => {
                if (!negotiateForm.message.trim()) {
                  toast.error(lang === 'fr' ? 'Message requis' : 'Message required');
                  return;
                }
                try {
                  const payload = { message: negotiateForm.message };
                  if (negotiateForm.counter_price) payload.counter_price = parseFloat(negotiateForm.counter_price);
                  await api.post(`/projects/${negotiateOpen.id}/sub-negotiate`, payload);
                  toast.success(lang === 'fr' ? 'Message envoyé à ProdLink' : 'Message sent to ProdLink');
                  setNegotiateOpen(null);
                  setNegotiateForm({ message: '', counter_price: '' });
                  setProjectDetail(pd => pd ? { ...pd, order_negotiating: true, order_negotiation: { at: new Date().toISOString(), message: payload.message, counter_price: payload.counter_price } } : pd);
                  fetchAll();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
              }}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-semibold"
              data-testid="negotiate-submit-btn"
            >
              <Send className="w-4 h-4 mr-1.5" /> {lang === 'fr' ? 'Envoyer' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NDA signature dialog */}
      <NdaSignDialog
        open={ndaDialogOpen}
        onClose={() => setNdaDialogOpen(false)}
        onSigned={async () => {
          setNdaDialogOpen(false);
          try {
            const { data } = await api.get('/nda/status');
            setNdaStatus(data);
          } catch (err) { console.debug('[sub-portal] silent fetch failure:', err?.message); }
        }}
        defaultName={user?.name || ''}
        companyName={user?.company || ''}
        blocking={false}
      />

      {/* Price-request negotiation dialog (sub side) */}
      <NegotiationDialog
        open={!!prNegotiate}
        onOpenChange={(v) => !v && setPrNegotiate(null)}
        viewerRole="subcontractor"
        prId={prNegotiate?.pr_id}
        responseId={prNegotiate?.response_id}
        lang={lang}
        initialPrice={prNegotiate?.price}
        currency={prNegotiate?.currency || 'EUR'}
        clientLabel={prNegotiate?.client_name || ''}
        onChange={fetchAll}
      />
    </div>
  );
}

// Photo thumbnail with auth-fetched blob URL
function PhotoThumb({ file, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    api.get(`/files/download/${file.id}`, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setSrc(currentUrl);
      })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [file.id]);
  const isImage = (file.content_type || '').startsWith('image/');
  return (
    <div className="relative group bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden aspect-square" data-testid={`photo-${file.id}`}>
      {isImage && src ? (
        <img src={src} alt={file.original_filename} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-2">
          <Download className="w-6 h-6 text-zinc-500 mb-1" />
          <p className="text-[10px] text-zinc-400 text-center truncate w-full">{file.original_filename}</p>
        </div>
      )}
      <button onClick={onRemove} className="absolute top-1 right-1 bg-zinc-950/80 text-rose-400 hover:text-rose-300 p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}
